import React, { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Heart, Sparkles, Star, Check, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import { toast } from "react-hot-toast";

const TIERS = [
    {
        id: "PATHFINDER",
        price: 99,
        icon: <Heart className="w-6 h-6" />,
        color: "text-rose-500",
        bgColor: "bg-rose-50 dark:bg-rose-900/10",
        perks: ["Unlock 30D Mood History", "Pathfinder Badge", "Early access to new tools"]
    },
    {
        id: "GUARDIAN",
        price: 499,
        icon: <Sparkles className="w-6 h-6" />,
        color: "text-brand-blue",
        bgColor: "bg-blue-50 dark:bg-blue-900/10",
        perks: ["All Pathfinder perks", "Lifetime History Access", "Ad-free experience", "Premium Sanctuary Themes"],
        featured: true
    },
    {
        id: "SAGE",
        price: 999,
        icon: <Star className="w-6 h-6" />,
        color: "text-amber-500",
        bgColor: "bg-amber-50 dark:bg-amber-900/10",
        perks: ["All Guardian perks", "Founding Member Status", "1-on-1 Guidance Session", "Help keep AYI free for all"]
    }
];

export default function Sustain() {
    const { t } = useLanguage();
    const { user } = useAuth();
    const [loading, setLoading] = useState(null);

    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleSupport = async (tier) => {
        if (!user) {
            toast.error("Please login to support the sanctuary.");
            return;
        }

        setLoading(tier.id);

        try {
            const res = await loadRazorpay();
            if (!res) {
                toast.error("Razorpay SDK failed to load. Are you online?");
                return;
            }

            // 1. Create Order
            const { data: order } = await api.post("/sustain/create-order", { tier: tier.id });

            // 2. Initialize Razorpay Checkout
            const options = {
                key: order.key,
                amount: order.amount,
                currency: order.currency,
                name: "Antaryatra Sanctuary",
                description: `Support Tier: ${tier.id}`,
                image: "/logo192.png",
                order_id: order.orderId,
                handler: async (response) => {
                    // 3. Verify Payment
                    try {
                        const verifyRes = await api.post("/sustain/verify", {
                            ...response,
                            tier: tier.id
                        });
                        if (verifyRes.data.success) {
                            toast.success("Thank you! Your features are now unlocked.");
                            window.location.href = "/dashboard";
                        }
                    } catch (e) {
                        toast.error("Verification failed. Please contact support.");
                    }
                },
                prefill: {
                    name: user.name,
                    email: user.email,
                },
                theme: {
                    color: "#3B82F6",
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (error) {
            console.error("Payment Start Error:", error);
            toast.error("Could not initiate payment.");
        } finally {
            setLoading(null);
        }
    };

    return (
        <div className="min-h-screen bg-spiritual-white dark:bg-gray-950 text-gray-800 dark:text-gray-100 font-body py-20 px-6">
            <SEO 
                title={t('sustain_title') || "Sustain the Sanctuary"} 
                description={t('sustain_sub') || "Help us keep the light of awareness alive."} 
                url="/sustain" 
            />

            <main className="max-w-5xl mx-auto space-y-12">
                <header className="text-center space-y-4">
                    <div className="flex justify-center text-brand-blue mb-2">
                        <Shield className="w-10 h-10 opacity-80" />
                    </div>
                    <h1 className="text-3xl md:text-5xl font-display font-medium">Sustain the Sanctuary</h1>
                    <p className="text-sm md:text-lg text-gray-500 dark:text-gray-400 font-light max-w-2xl mx-auto">
                        Antaryatra is built on the principle of Dana (generosity). Your support keeps our tools free for those in crisis and allows us to build deep, non-extractive insights.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {TIERS.map((tier) => (
                        <motion.div
                            key={tier.id}
                            whileHover={{ y: -5 }}
                            className={`relative bg-white dark:bg-gray-900 border ${tier.featured ? 'border-brand-blue ring-1 ring-brand-blue' : 'border-gray-100 dark:border-gray-800'} rounded-3xl p-8 flex flex-col shadow-xl shadow-blue-900/5`}
                        >
                            {tier.featured && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-blue text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                    Most Impactful
                                </div>
                            )}
                            
                            <div className={`${tier.bgColor} ${tier.color} w-12 h-12 rounded-2xl flex items-center justify-center mb-6`}>
                                {tier.icon}
                            </div>

                            <h3 className="text-xl font-bold mb-1">{tier.id.charAt(0) + tier.id.slice(1).toLowerCase()}</h3>
                            <div className="flex items-baseline gap-1 mb-6">
                                <span className="text-3xl font-bold">₹{tier.price}</span>
                                <span className="text-xs text-gray-400">one-time support</span>
                            </div>

                            <ul className="space-y-4 mb-8 flex-1">
                                {tier.perks.map((perk, i) => (
                                    <li key={i} className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                        {perk}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handleSupport(tier)}
                                disabled={loading !== null}
                                className={`w-full py-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                    tier.featured 
                                    ? 'bg-brand-blue text-white shadow-lg shadow-blue-500/20 hover:opacity-90' 
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                {loading === tier.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Support Now'}
                            </button>
                        </motion.div>
                    ))}
                </div>

                <div className="text-center pt-8">
                    <p className="text-xs text-gray-400 italic">
                        "Generosity is the most natural outward expression of an inner awareness."
                    </p>
                </div>
            </main>
        </div>
    );
}
